import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import SafeIcon from '../../components/common/SafeIcon';
import ReelCropSettings from '../../components/training/ReelCropSettings';
import TimestampInput from '../../components/training/TimestampInput';
import { useAuth } from '../../context/AuthContext';
import {
  fetchReelById,
  getVideoMetadataFromUrl,
  publishReelDraft,
  saveReelDraft,
} from '../../services/training';
import { DEFAULT_CROP_SETTINGS } from '../../utils/reelCrop';
import { formatTimestamp, parseTimestampInput } from '../../utils/videoTime';

function FieldLabel({ children, required }) {
  return (
    <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-2">
      {children}
      {required && <span className="text-brand-cyan ml-1">*</span>}
    </label>
  );
}

function TextArea({ className = '', ...props }) {
  return (
    <textarea
      className={`w-full bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-xl py-3 px-4 text-sm text-[var(--text-main)] placeholder-[var(--text-secondary)]/50 focus:outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green transition-all resize-y min-h-[120px] ${className}`}
      {...props}
    />
  );
}

export default function ReelDraftPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canManageTraining, loading: authLoading } = useAuth();

  const [reel, setReel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [caption, setCaption] = useState('');
  const [clipStart, setClipStart] = useState('');
  const [clipEnd, setClipEnd] = useState('');
  const [aspectRatio, setAspectRatio] = useState(null);
  const [cropSettings, setCropSettings] = useState({ ...DEFAULT_CROP_SETTINGS });
  const [videoDuration, setVideoDuration] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchReelById(id)
      .then(async (row) => {
        if (!active) return;
        if (!row) {
          setError('Borrador no encontrado');
          setReel(null);
          return;
        }
        if (!row.isDraft) {
          navigate(row.isArchived ? '/app/entrenamientos/archivo' : '/app', { replace: true });
          return;
        }
        setReel(row);
        setCaption(row.caption || '');
        setClipStart(row.clipStartSeconds != null ? formatTimestamp(row.clipStartSeconds) : '');
        setClipEnd(row.clipEndSeconds != null ? formatTimestamp(row.clipEndSeconds) : '');
        setCropSettings({
          cropMode: row.cropMode ?? DEFAULT_CROP_SETTINGS.cropMode,
          focalX: row.focalX ?? DEFAULT_CROP_SETTINGS.focalX,
          focalY: row.focalY ?? DEFAULT_CROP_SETTINGS.focalY,
        });

        let ratio = row.sourceAspectRatio;
        if (row.videoUrl) {
          try {
            const meta = await getVideoMetadataFromUrl(row.videoUrl);
            if (!active) return;
            ratio = ratio || meta.aspectRatio;
            setVideoDuration(meta.durationSeconds);
          } catch {
            // optional metadata
          }
        }
        if (active) setAspectRatio(ratio);
      })
      .catch((err) => {
        if (active) setError(err.message || 'No se pudo cargar el borrador');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [id, navigate]);

  const parseClipForDraft = () => {
    if (!clipStart.trim() && !clipEnd.trim()) {
      return { clipStartSeconds: null, clipEndSeconds: null };
    }
    if (!clipStart.trim() || !clipEnd.trim()) {
      // Incomplete pair — keep unset on draft save
      return { clipStartSeconds: null, clipEndSeconds: null };
    }
    try {
      const clipStartSeconds = parseTimestampInput(clipStart, 'Inicio del clip');
      const clipEndSeconds = parseTimestampInput(clipEnd, 'Fin del clip');
      if (clipEndSeconds <= clipStartSeconds) {
        return { clipStartSeconds: null, clipEndSeconds: null };
      }
      if (videoDuration && clipEndSeconds > videoDuration) {
        return { clipStartSeconds: null, clipEndSeconds: null };
      }
      return { clipStartSeconds, clipEndSeconds };
    } catch {
      return { clipStartSeconds: null, clipEndSeconds: null };
    }
  };

  const parseClipForPublish = () => {
    let clipStartSeconds = null;
    let clipEndSeconds = null;
    if (clipStart.trim() || clipEnd.trim()) {
      clipStartSeconds = parseTimestampInput(clipStart || '0', 'Inicio del clip');
      clipEndSeconds = parseTimestampInput(clipEnd, 'Fin del clip');
      if (clipEndSeconds <= clipStartSeconds) {
        throw new Error('El fin del clip debe ser posterior al inicio');
      }
      if (videoDuration && clipEndSeconds > videoDuration) {
        throw new Error(`El clip no puede terminar después de ${formatTimestamp(videoDuration)}`);
      }
    }
    return { clipStartSeconds, clipEndSeconds };
  };

  const handleSaveDraft = async () => {
    setError('');
    setInfo('');
    setSubmitting(true);
    try {
      const clips = parseClipForDraft();
      await saveReelDraft(reel.id, {
        caption,
        ...clips,
        sourceAspectRatio: aspectRatio,
        ...cropSettings,
      });
      setInfo('Borrador guardado. Puedes volver más tarde sin volver a subir el video.');
    } catch (err) {
      setError(err.message || 'No se pudo guardar el borrador');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePublish = async () => {
    setError('');
    setInfo('');
    if (!caption.trim()) {
      setError('Añade un texto o caption para el reel');
      return;
    }
    setSubmitting(true);
    try {
      const clips = parseClipForPublish();
      await publishReelDraft(reel.id, {
        caption: caption.trim(),
        ...clips,
        sourceAspectRatio: aspectRatio,
        ...cropSettings,
      });
      navigate('/app', { replace: true, state: { uploadSuccess: 'reel', reelId: reel.id } });
    } catch (err) {
      setError(err.message || 'No se pudo publicar el reel');
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center text-[var(--text-secondary)] text-sm font-bold uppercase tracking-widest">
        Cargando…
      </div>
    );
  }

  if (!canManageTraining) {
    return <Navigate to="/app/entrenamientos" replace />;
  }

  if (!reel) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-10">
        <p className="text-red-300 text-sm">{error || 'Borrador no encontrado'}</p>
        <Link to="/app/entrenamientos/subir" className="text-brand-cyan text-sm font-bold mt-4 inline-block">
          Volver a subir
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-10 pb-28">
      <div className="flex items-center gap-4 mb-8">
        <Link
          to="/app/entrenamientos/subir"
          className="w-10 h-10 rounded-full bg-[var(--bg-surface)] border border-[var(--border-subtle)] flex items-center justify-center text-[var(--text-main)] hover:bg-[var(--border-subtle)] transition-colors"
        >
          <SafeIcon name="ArrowLeft" className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-[var(--text-main)] uppercase tracking-tight italic">
            Detalles del reel
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            Paso 2 de 2 — completa los datos. Guarda como borrador o publica cuando esté listo.
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-xl border border-red-400/40 bg-red-500/10 text-red-300 text-sm">
          {error}
        </div>
      )}
      {info && (
        <div className="mb-6 p-4 rounded-xl border border-brand-green/40 bg-brand-green/10 text-[var(--text-main)] text-sm">
          {info}
        </div>
      )}

      {reel.videoUrl && (
        <section className="mb-8 p-4 rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-surface)] space-y-4">
          <FieldLabel>Video subido</FieldLabel>
          <video
            src={reel.videoUrl}
            controls
            className="w-full rounded-xl border border-[var(--border-subtle)] max-h-72 bg-black"
          />
          {aspectRatio && (
            <ReelCropSettings
              videoSrc={reel.videoUrl}
              aspectRatio={aspectRatio}
              value={cropSettings}
              onChange={setCropSettings}
              disabled={submitting}
            />
          )}
        </section>
      )}

      <div className="space-y-6">
        <div>
          <FieldLabel required>Caption</FieldLabel>
          <TextArea
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            disabled={submitting}
            placeholder="Texto breve que aparecerá en el feed de reels"
          />
        </div>

        <section className="p-6 rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-surface)] space-y-4">
          <FieldLabel>Recorte de tiempo (opcional)</FieldLabel>
          <p className="text-xs text-[var(--text-secondary)]">
            Usa el reproductor para encontrar el inicio y fin. Formato mm:ss.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <FieldLabel>Inicio</FieldLabel>
              <TimestampInput
                value={clipStart}
                onChange={(e) => setClipStart(e.target.value)}
                disabled={submitting}
              />
            </div>
            <div>
              <FieldLabel>Fin</FieldLabel>
              <TimestampInput
                value={clipEnd}
                onChange={(e) => setClipEnd(e.target.value)}
                disabled={submitting}
              />
            </div>
          </div>
        </section>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={handleSaveDraft}
            disabled={submitting}
            className="flex-1 flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-[var(--border-subtle)] bg-[var(--bg-surface)] font-black text-xs uppercase tracking-widest text-[var(--text-main)] hover:border-brand-cyan disabled:opacity-50"
          >
            {submitting ? (
              <SafeIcon name="Loader" className="w-5 h-5 animate-spin" />
            ) : (
              <SafeIcon name="FileText" className="w-5 h-5" />
            )}
            Guardar como borrador
          </button>
          <button
            type="button"
            onClick={handlePublish}
            disabled={submitting}
            className="flex-1 flex items-center justify-center gap-2 gfi-gradient-bg text-brand-navy px-8 py-4 rounded-full font-black text-xs uppercase tracking-widest hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100 shadow-xl"
          >
            {submitting ? (
              <SafeIcon name="Loader" className="w-5 h-5 animate-spin" />
            ) : (
              <SafeIcon name="Upload" className="w-5 h-5" />
            )}
            Publicar reel
          </button>
        </div>
      </div>
    </div>
  );
}
